import { useState } from "react";

const DESK_COLORS = { SALES: "#FF5C5C", "CLIENT SERVICES": "#F59E0B", "BIZ DEV": "#FFFFFF", "PROJECT DESK": "#4ADE80" };

const highlight = (text, q) => {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-[#DC2626] text-white px-0.5 rounded">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

export const ConversationsView = ({ conversations, api, authHeaders }) => {
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const open = async (id) => {
    setSelected(id);
    const d = await fetch(`${api}/admin/conversations/${id}`, { headers: authHeaders }).then((r) => r.json());
    setMessages(d.messages);
  };

  const search = async (e) => {
    e?.preventDefault();
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const d = await fetch(`${api}/admin/search?q=${encodeURIComponent(query.trim())}`, { headers: authHeaders }).then((r) => r.json());
      setResults(d);
    } catch {}
    setSearching(false);
  };

  const clear = () => { setQuery(""); setResults(null); };

  if (!conversations) return <p className="text-base font-medium text-[#D6D6D6]">Loading...</p>;

  return (
    <div className="space-y-4" data-testid="conversations-view">
      <form onSubmit={search} className="flex gap-2">
        <div className="relative flex-1">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search every conversation — a keyword, a company, anything a lead said..."
            data-testid="conversation-search-input"
            className="w-full rounded-xl bg-[#050505] border border-[#2E2E2E] pl-4 pr-16 py-3 text-[15px] font-medium text-white placeholder:text-[#D6D6D6] outline-none focus:border-[#FF5C5C]"
          />
          {results && (
            <button type="button" onClick={clear} data-testid="conversation-search-clear" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-[#D6D6D6] hover:text-white">
              Clear
            </button>
          )}
        </div>
        <button type="submit" disabled={searching || query.trim().length < 2} data-testid="conversation-search-button"
          className="text-[15px] font-semibold rounded-xl bg-white text-black px-5 py-2.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
          {searching ? "..." : "Search"}
        </button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] max-h-[70vh] overflow-y-auto chat-scroll">
          {results ? (
            <>
              <p className="text-[15px] font-semibold text-[#D6D6D6] p-4 border-b border-[#2E2E2E]" data-testid="search-results-summary">
                {results.results.length} conversation(s) · {results.total_hits} hit(s) for "{results.query}"
              </p>
              {results.results.map((r, ri) => (
                <button key={`${r.session_id}-${ri}`} onClick={() => open(r.session_id)} data-testid="search-result-row"
                  className={`w-full text-left p-4 border-b border-[#2E2E2E] hover:bg-[#1A1A1A] transition-colors duration-150 ${selected === r.session_id ? "bg-[#2E2E2E]" : ""}`}>
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-[15px] font-semibold text-[#D6D6D6]">{r.session_id.slice(0, 8)}...</span>
                    <span className="flex items-center gap-2">
                      {r.desk && <span className="text-[14px] font-bold" style={{ color: DESK_COLORS[r.desk] || "#FF5C5C" }}>{r.desk}</span>}
                      <span className="text-[14px] font-semibold text-[#D6D6D6] border border-[#2E2E2E] rounded-full px-2.5 py-0.5">{r.hits} hit{r.hits > 1 ? "s" : ""}</span>
                    </span>
                  </div>
                  {r.matches.map((m, i) => (
                    <p key={i} className="text-[14px] font-medium text-[#D6D6D6] mt-1.5 leading-relaxed">
                      <span className="text-[#D6D6D6] font-semibold">{m.role}: </span>{highlight(m.snippet, results.query)}
                    </p>
                  ))}
                </button>
              ))}
              {results.results.length === 0 && <p className="text-[15px] font-medium text-[#D6D6D6] p-4">Nothing matched that. Try another word.</p>}
            </>
          ) : (
            <>
              {conversations.map((c, ci) => (
                <button key={`${c.id}-${ci}`} onClick={() => open(c.id)} data-testid="conversation-row"
                  className={`w-full text-left p-4 border-b border-[#2E2E2E] hover:bg-[#1A1A1A] transition-colors duration-150 ${selected === c.id ? "bg-[#2E2E2E]" : ""}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-[15px] font-semibold text-[#D6D6D6]">{c.id.slice(0, 8)}...</span>
                    <span className="text-[14px] font-semibold text-[#D6D6D6] border border-[#2E2E2E] rounded-full px-2.5 py-0.5">{c.intent || "general"}</span>
                  </div>
                  <p className="text-[14px] font-medium text-[#D6D6D6] mt-1">
                    {c.message_count} messages · {new Date(c.last_activity).toLocaleString()}
                  </p>
                </button>
              ))}
              {conversations.length === 0 && <p className="text-[15px] font-medium text-[#D6D6D6] p-4">No conversations yet</p>}
            </>
          )}
        </div>

        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-5 max-h-[70vh] overflow-y-auto chat-scroll">
          {!selected && <p className="text-[15px] font-medium text-[#D6D6D6]">Select a conversation</p>}
          {selected && messages.map((m) => (
            <div key={m.id} className="mb-5">
              <p className={`text-[14px] font-bold mb-1.5 ${m.role === "agent" ? "text-[#FF5C5C]" : "text-[#D6D6D6]"}`}>{m.role === "agent" ? "Agent" : "Visitor"}</p>
              <p className="text-[15px] font-medium text-[#D6D6D6] leading-relaxed whitespace-pre-wrap">
                {results ? highlight(m.content, results.query) : m.content}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
